import { FC, useMemo } from "react";

import { ReferencedPayment } from "./ReferencedPayment/ReferencedPayment";

import { ChoosePaymentProvider } from "../contexts/ChoosePaymentContext";
import type { DonorAssistantChat } from "../helpers/useDonorAssistant";

import { trpc } from "@/common/helpers/trpc";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardTypography } from "@/dashboard-design-system/Typography";
import { Chat } from "@/features/Assistant/Chat/Chat";
import type { AssistantChatViewProps } from "@/features/Assistant/helpers/types";
import { useAssistantUnavailableText } from "@/features/Assistant/helpers/useAssistantUnavailableText";

export const ChatView: FC<AssistantChatViewProps<DonorAssistantChat>> = ({ chat, onStartNew, onMessageSent }) => {
  const { t } = useTranslate();
  const { choosePayment, referencedPayment, clearReferencedPayment } = chat;
  const choosePaymentContext = useMemo(() => ({ choosePayment }), [choosePayment]);
  const unavailableText = useAssistantUnavailableText(
    trpc.getDonorAssistantAvailability.useQuery(),
    chat.errorCode === "rate_limited"
  );
  const errorText =
    unavailableText ?? (chat.errorCode === "rate_limited" ? t("dashboard", "donorAssistant.errors.rateLimited") : null);
  return (
    <ChoosePaymentProvider value={choosePaymentContext}>
      <Chat
        {...chat}
        isComposerDisabled={chat.isComposerDisabled || Boolean(unavailableText)}
        suggestions={[
          t("dashboard", "donorAssistant.suggestions.recurring"),
          t("dashboard", "donorAssistant.suggestions.receipt"),
          t("dashboard", "donorAssistant.suggestions.unrecognizedCharge"),
        ]}
        composerAccessory={
          referencedPayment && <ReferencedPayment payment={referencedPayment} onRemove={clearReferencedPayment} />
        }
        errorNotice={
          errorText ? (
            <DashboardTypography role="alert" variant="body2" vibe="text-danger-intense" sx={{ pb: 1 }}>
              {errorText}
            </DashboardTypography>
          ) : undefined
        }
        onStartNew={onStartNew}
        onMessageSent={onMessageSent}
      />
    </ChoosePaymentProvider>
  );
};
